/**
 * SkyNexa — technology matrix search and filtering.
 *
 * Operates on the grid generated by scripts/build-stack-page.mjs. Two
 * independent controls that compose: a category filter (which categories are
 * shown) and a free-text search (which individual technologies match).
 *
 * Accessibility notes:
 *   - the result count is a polite live region, so filtering is announced
 *     rather than being a silent visual change;
 *   - filter buttons carry aria-pressed, so their state is exposed;
 *   - hiding uses the `hidden` property, which removes elements from the
 *     accessibility tree as well as from view.
 *
 * @module stack-filter
 */

/**
 * Decide what a query matches. Pure, so the matching rules are unit-tested
 * without a DOM.
 *
 * @param {string} query Raw search input.
 * @param {{ name: string, note?: string, category: string }} entry
 * @returns {boolean}
 */
export function matchesQuery(query, entry) {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  // Every whitespace-separated term must appear somewhere in the entry, so
  // "aws iam" matches "AWS IAM / Cognito" but "aws mongo" matches nothing.
  const haystack = `${entry.name} ${entry.note ?? ''} ${entry.category}`.toLowerCase();
  return needle.split(/\s+/).every((term) => haystack.includes(term));
}

/** Count how many chips a given filter + query combination leaves visible. */
export function countVisible(entries, { filter = 'all', query = '' } = {}) {
  return entries.filter(
    (entry) => (filter === 'all' || entry.category === filter) && matchesQuery(query, entry),
  ).length;
}

export function initStackFilter(root = document) {
  const grid = root.getElementById?.('stack-grid') ?? root.querySelector('#stack-grid');
  if (!grid) return null;

  const searchInput = root.querySelector('#stack-search');
  const buttons = [...root.querySelectorAll('.stack-filter-btn')];
  const countNode = root.querySelector('[data-stack-count]');
  const emptyNode = root.querySelector('[data-stack-empty]');
  const categories = [...grid.querySelectorAll('.tech-category')];

  // Snapshot the DOM once into a plain array; re-reading text content on every
  // keystroke is the thing that makes this kind of filter feel sluggish.
  const entries = categories.flatMap((category) =>
    [...category.querySelectorAll('.tech-chip')].map((element) => ({
      element,
      category: category.dataset.category,
      name: element.querySelector('.tech-chip__name')?.textContent ?? '',
      note: element.querySelector('.tech-chip__note')?.textContent ?? '',
    })),
  );

  let activeFilter = 'all';
  let activeQuery = '';

  function apply() {
    let visible = 0;

    for (const category of categories) {
      const inFilter = activeFilter === 'all' || category.dataset.category === activeFilter;
      let categoryHits = 0;

      for (const entry of entries) {
        if (entry.category !== category.dataset.category) continue;
        const hit = inFilter && matchesQuery(activeQuery, entry);
        entry.element.hidden = !hit;
        entry.element.classList.toggle('is-match', hit && activeQuery.trim().length > 0);
        if (hit) categoryHits += 1;
      }

      category.hidden = categoryHits === 0;
      visible += categoryHits;
    }

    if (countNode) {
      countNode.textContent =
        visible === entries.length
          ? `Showing all ${entries.length} technologies.`
          : `Showing ${visible} of ${entries.length} technologies.`;
    }
    if (emptyNode) emptyNode.hidden = visible > 0;
  }

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      activeFilter = button.dataset.filter;
      buttons.forEach((other) => {
        const isActive = other === button;
        other.classList.toggle('active', isActive);
        other.setAttribute('aria-pressed', String(isActive));
      });
      apply();
    });
  });

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      activeQuery = searchInput.value;
      apply();
    });
    // Escape clears the search rather than only blurring the field.
    searchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && searchInput.value) {
        event.preventDefault();
        searchInput.value = '';
        activeQuery = '';
        apply();
      }
    });
  }

  apply();
  return { entries, apply };
}

if (typeof document !== 'undefined') {
  const boot = () => initStackFilter(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
